import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  bool,
  func,
  shape,
  string,
  arrayOf
} from 'prop-types';
import classNames from 'classnames';
import {
  Alert,
  Button,
  Dropdown,
  MenuItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
  Typography
} from '@one-thd/sui-atomic-components';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import _isEmpty from 'lodash/isEmpty';
import { useDesignAddTouchCabinet } from '../hooks/cabinetVisualizerHooks';
import { cabinetToBoundingBox,
  createBoundingBoxFromTouchpoint } from '../utils';
import { useCabinetVisualizerStore } from '../../../../store/index';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import {
  DEFAULT_WALLSIZE,
  selectDesignAnchorSide,
  selectDesignProducts,
  selectSpacerChoices
} from '../../../../store/cabinetVisualizer/selectors';
import { EMPTY_OBJECT } from '../../../../utils/empty-reference';
import { CabinetShape } from './type';
import {
  transformSpacerChoices,
  getSpacerTypesByCategory,
  getSpacerTypeById,
  getSpacerTypeByChoiceId
} from '../../../../utils/transformSpacerData';
import { CABINET_SKU, CUSTOM_SPACER_TYPE_ID } from '../../cabinetConstants';

const decimalRegex = /^(\d+(\.\d*)?|\.\d+)$/;

const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';

export const SpacerModal = ({
  designId,
  openSpacer,
  closeSpacer,
  touchpoint,
  editMode: editModeProp,
  editSpacerConfig,
  editSpacer,
  isEditCabinetValid,
  cabinets
}) => {

  const { cabinetVisualizer } = useCabinetVisualizerStore();

  // Derive editMode from editSpacerConfig data - more reliable than prop which may have timing issues
  // If we have a choiceId or width in editSpacerConfig, we're editing an existing spacer
  const editMode = editModeProp || Boolean(editSpacerConfig?.choiceId || editSpacerConfig?.width);

  // Get spacer choices from store and transform them
  const spacerChoices = selectSpacerChoices(cabinetVisualizer);
  const allSpacerTypes = useMemo(
    () => transformSpacerChoices(spacerChoices),
    [spacerChoices]
  );

  // Determine if this is a wall or base spacer based on touchpoint type or SKU (for edit mode)
  const spacerCategory = useMemo(() => {
    // In edit mode, use SKU to determine category
    if (editMode && editSpacerConfig.sku) {
      return editSpacerConfig.sku === CABINET_SKU.WALL_SPACER ? 'wall' : 'base';
    }
    // In add mode, use touchpoint type
    return touchpoint?.type?.[0]?.toLowerCase() === 'wall' ? 'wall' : 'base';
  }, [editMode, editSpacerConfig.sku, touchpoint?.type]);

  const availableSpacerTypes = useMemo(
    () => getSpacerTypesByCategory(allSpacerTypes, spacerCategory),
    [allSpacerTypes, spacerCategory]
  );

  const defaultSpacerType = availableSpacerTypes.find(
    (type) => type.id === CUSTOM_SPACER_TYPE_ID
  )?.id || availableSpacerTypes[0]?.id;

  // Look up spacer type by ChoiceId when editing - ChoiceId is always persisted
  const spacerTypeFromChoiceId = useMemo(() => {
    if (!editMode || !editSpacerConfig.choiceId || !allSpacerTypes.length) {
      return null;
    }
    const matchedType = getSpacerTypeByChoiceId(allSpacerTypes, editSpacerConfig.choiceId);
    return matchedType?.id || null;
  }, [editMode, editSpacerConfig.choiceId, allSpacerTypes]);

  // Determine the initial spacer type
  // For edit mode: use ChoiceId lookup (always available from API)
  // For add mode: use default
  const initialSpacerType = editMode
    ? (spacerTypeFromChoiceId || defaultSpacerType)
    : defaultSpacerType;

  const [spacerType, setSpacerType] = useState(initialSpacerType || '');

  const [name, setName] = useState(editMode ? editSpacerConfig.name : '');
  const [width, setWidth] = useState(editMode ? editSpacerConfig.width : '');
  const [height, setHeight] = useState(editMode ? editSpacerConfig.height : '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isMobile = useIsMobileDevice();
  const addTouchPointCabinet = useDesignAddTouchCabinet(designId, cabinets, enableTouchPointsOnBothSides);
  const wallSize = DEFAULT_WALLSIZE; // selectDesignMatrixSizeWithDefault(cabinetVisualizer, designId);
  const anchorPosition = selectDesignAnchorSide(cabinetVisualizer, designId);

  const products = (designId && cabinetVisualizer) ? selectDesignProducts(cabinetVisualizer, designId) : [];
  // All the info the the currently selected cabinet to add
  const cabinetBoundings = products?.map((cab) => cabinetToBoundingBox(cab));

  // Get the current spacer type info for name resolution
  const currentSpacerType = getSpacerTypeById(allSpacerTypes, spacerType);
  const isCustomSpacer = spacerType === CUSTOM_SPACER_TYPE_ID;
  // Use type label as name for preset spacers, user-entered name for custom spacers
  const spacerName = isCustomSpacer ? name : (currentSpacerType?.label || name);

  const cabinetInfo = {
    width: Number(width),
    height: Number(height),
    color: 'grey',
    itemId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
    parentId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
    name: spacerName,
    spacerType,
    ChoiceId: currentSpacerType?.choiceId,
    productType: touchpoint?.type ? touchpoint.type[0] : 'Wall',
    direction: touchpoint?.direction || 'left',
  };
  const boundingBox = createBoundingBoxFromTouchpoint(touchpoint || {}, {
    ...cabinetInfo,
    maxHeight: wallSize.Rows,
    maxWidth: wallSize.Columns,
    anchorPosition
  });

  const isCabinetColliding = () => {
    return !isEditCabinetValid(cabinetInfo);
  };

  useEffect(() => {
    if (editMode && editSpacerConfig !== EMPTY_OBJECT) {
      // Use spacerTypeFromChoiceId for reliable lookup, fallback to default
      setSpacerType(spacerTypeFromChoiceId || defaultSpacerType);
      setName(editSpacerConfig.name || '');
      setWidth(editSpacerConfig.width || '');
      setHeight(editSpacerConfig.height || '');
    } else {
      // Reset to defaults when opening modal for a new spacer
      setSpacerType(defaultSpacerType);
      setName('');
      setWidth('');
      setHeight('');
    }
  }, [editMode, editSpacerConfig, defaultSpacerType, openSpacer, spacerTypeFromChoiceId]);

  // Handle spacer type change and auto-fill dimensions for preset types
  const handleSpacerTypeChange = (event) => {
    const newType = event.target.value;
    setSpacerType(newType);

    const selectedType = getSpacerTypeById(allSpacerTypes, newType);
    if (selectedType) {
      // Clear name and dimensions when switching to custom spacer
      if (newType === CUSTOM_SPACER_TYPE_ID) {
        setName('');
        setWidth('');
        setHeight('');
      } else {
        // Auto-fill name with type label if empty
        if (!name) {
          setName(selectedType.label);
        }
        // Auto-fill dimensions for preset types
        if (selectedType.defaultWidth !== null) {
          setWidth(String(selectedType.defaultWidth));
        }
        if (selectedType.defaultHeight !== null) {
          setHeight(String(selectedType.defaultHeight));
        }
      }
    }
  };

  const addSpacerConfig = useCallback(() => (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeSpacer(event);

    // Build cabinetInfo here to ensure we have the latest state values
    const currentType = getSpacerTypeById(allSpacerTypes, spacerType);
    const resolvedName = spacerType === CUSTOM_SPACER_TYPE_ID ? name : (currentType?.label || name);

    const finalCabinetInfo = {
      width: Number(width),
      height: Number(height),
      color: 'grey',
      itemId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
      parentId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
      name: resolvedName,
      spacerType,
      ChoiceId: currentType?.choiceId,
      productType: touchpoint?.type ? touchpoint.type[0] : 'Wall',
      direction: touchpoint?.direction || 'left',
    };

    const finalBoundingBox = createBoundingBoxFromTouchpoint(touchpoint || {}, {
      ...finalCabinetInfo,
      maxHeight: wallSize.Rows,
      maxWidth: wallSize.Columns,
      anchorPosition
    });

    if (editMode) {
      editSpacer(finalCabinetInfo);
    } else {
      addTouchPointCabinet({
        cabinetInfo: finalCabinetInfo,
        boundingBox: finalBoundingBox
      });
    }
  }, [
    allSpacerTypes, spacerType, name, width, height, touchpoint,
    wallSize, anchorPosition, editMode, editSpacer, addTouchPointCabinet, closeSpacer
  ]);

  const handleName = (event) => {
    const nameText = event.target.value;
    if (nameText.length <= 20) {
      setName(event.target.value);
    }
  };

  const handleWidth = (event) => {
    if (_isEmpty(event.target.value) || decimalRegex.test(event.target.value)) {
      setWidth(event.target.value);
    }
  };

  const handleHeight = (event) => {
    if (_isEmpty(event.target.value) || decimalRegex.test(event.target.value)) {
      setHeight(event.target.value);
    }
  };

  const fieldValidations = () => {
    if (_isEmpty(String(width)) || _isEmpty(String(height))) {
      return true;
    }
    // Name is only required for custom spacers
    if (isCustomSpacer && _isEmpty(name)) {
      return true;
    }
    if (Number(width) <= 0 || Number(height) <= 0) {
      return true;
    }
    return false;
  };

  // Apply spacing for dropdown when not on custom spacer and is opened.
  const nameFieldContainerClasses = classNames('sui-overflow-hidden', {
    'sui-h-auto sui-visible': isCustomSpacer,
    'sui-h-26 sui-invisible': !isCustomSpacer && isDropdownOpen && !isMobile,
    'sui-h-0 sui-invisible': !isCustomSpacer && (!isDropdownOpen || isMobile)
  });

  return (
    <>
      <Modal size="sm" open={openSpacer} onClose={closeSpacer} className="spacer-modal">
        <ModalHeader onClose={closeSpacer}>Add Spacer</ModalHeader>
        <ModalBody className="spacer-modal__body">
          <Row className="sui-pl-0 sui-pr-0">
            <Typography variant="body-base" color="primary">
              Spacers can used as placeholders for objects in your room
              (refrigerator, washer, etc.) or to maintain a desired amount
              of space between cabinets.
            </Typography>
          </Row>
          <Row className="sui-pl-0 sui-pr-0 sui-pt-1">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <Typography variant="body-base">
                Type <span className="sui-text-primary">*</span>
              </Typography>
              <div className="sui-pt-1">
                <Dropdown
                  id="spacer-type"
                  value={spacerType}
                  onChange={handleSpacerTypeChange}
                  onOpen={() => setIsDropdownOpen(true)}
                  onClose={() => setIsDropdownOpen(false)}
                  fullWidth
                  maxItems={5}
                  MenuProps={{
                    placement: 'bottom'
                  }}
                >
                  {availableSpacerTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Dropdown>
              </div>
            </Col>
          </Row>
          <div className={nameFieldContainerClasses}>
            <Row className="sui-pl-0 sui-pr-0 sui-pt-1">
              <Col className="sui-pl-0 sui-pr-0" fallback="12">
                <TextField
                  required
                  label="Name"
                  id="spacer-name"
                  fullWidth
                  placeholder=""
                  value={name}
                  helpMessage="20 character limit"
                  onChange={handleName}
                />
              </Col>
            </Row>
          </div>
          <Row className="sui-pl-0 sui-pr-0 sui-pt-1">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <form
                className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4"
                autoComplete="off"
                noValidate
              >
                <TextField
                  required
                  label="Width (in) "
                  id="spacer-width"
                  fullWidth
                  value={width}
                  onChange={handleWidth}
                />
                <TextField
                  required
                  label="Height (in) "
                  id="spacer-height"
                  fullWidth
                  value={height}
                  onChange={handleHeight}
                />
              </form>
            </Col>
          </Row>
          {isCabinetColliding() && openSpacer
          && (
            <Row className="sui-pl-0 sui-pr-0">
              <Col className="sui-pl-0 sui-pr-0" fallback="12">
                <Alert status="error">
                  This spacer in this specified position conflicts with other cabinets or spacers.
                  Please choose a different width/height or remove the obstructing cabinet or spacer.
                </Alert>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter position="end">
          <div className="sui-flex sui-gap-3">
            <Button variant="secondary" onClick={closeSpacer}>Cancel</Button>
            <Button
              variant="primary"
              onClick={addSpacerConfig()}
              disabled={fieldValidations() || isCabinetColliding()}
            >
              {editMode && editSpacerConfig?.width !== undefined ? 'Update Spacer' : 'Add Spacer'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

SpacerModal.propTypes = {
  designId: string.isRequired,
  openSpacer: bool,
  closeSpacer: func.isRequired,
  touchpoint: shape({}),
  editMode: bool,
  editSpacerConfig: shape({}),
  editSpacer: func,
  isEditCabinetValid: func,
  cabinets: arrayOf(CabinetShape)
};

SpacerModal.defaultProps = {
  openSpacer: false,
  touchpoint: null,
  editMode: false,
  editSpacerConfig: {},
  editSpacer: undefined,
  isEditCabinetValid: () => true,
  cabinets: []
};
